import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import ConfirmDialog from '../components/ConfirmDialog';
import { useT } from '../i18n/i18n';
import type { Invited, Parent, ParentAccess, Pupil } from './schoolApi';

const NONE: ParentAccess = { play: false, edit: false, talk: false };

function AccessFields({ id, value, onChange }: { id: string; value: ParentAccess; onChange: (a: ParentAccess) => void }) {
  const t = useT();
  const full = value.play && value.edit && value.talk;
  return (
    <fieldset className="sub-group parent-access">
      <legend className="field-label">{t('What they can do')}</legend>
      <label className="parent-access-row">
        <input type="checkbox" checked={full} onChange={(e) => onChange(e.target.checked ? { play: true, edit: true, talk: true } : NONE)} />
        <span><strong>{t('Full access')}</strong> — {t('everything below')}</span>
      </label>
      <label className="parent-access-row">
        <input type="checkbox" checked disabled />
        <span>{t('See progress and reports')}</span>
      </label>
      <label className="parent-access-row">
        <input id={`${id}-play`} type="checkbox" checked={value.play}
          onChange={(e) => onChange({ ...value, play: e.target.checked, edit: e.target.checked && value.edit })} />
        <span>{t('Practise their programs at home')}</span>
      </label>
      <label className="parent-access-row">
        <input type="checkbox" checked={value.edit}
          onChange={(e) => onChange({ ...value, edit: e.target.checked, play: e.target.checked || value.play })} />
        <span>{t('Change their programs and plans')}</span>
      </label>
      <label className="parent-access-row">
        <input type="checkbox" checked={value.talk} onChange={(e) => onChange({ ...value, talk: e.target.checked })} />
        <span>{t('Use and change their Talk Board')}</span>
      </label>
    </fieldset>
  );
}

function PupilPicker({ id, pupils, chosen, onChange }: { id: string; pupils: Pupil[]; chosen: string[]; onChange: (ids: string[]) => void }) {
  const t = useT();
  const [filter, setFilter] = useState('');
  const shown = pupils.filter((p) => !filter.trim() || p.name.toLowerCase().includes(filter.trim().toLowerCase()));
  return (
    <fieldset className="sub-group">
      <legend className="field-label">{t('Their child or children')}</legend>
      {pupils.length > 8 && (
        <div className="field" style={{ margin: 0 }}>
          <input aria-label={t('Find a pupil')} placeholder={t('Find a pupil')} value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>
      )}
      <div className="parent-pupils">
        {shown.map((p) => (
          <label key={p.id} className="parent-access-row">
            <input id={`${id}-${p.id}`} type="checkbox" checked={chosen.includes(p.id)}
              onChange={(e) => onChange(e.target.checked ? [...chosen, p.id] : chosen.filter((x) => x !== p.id))} />
            <span>{p.name}</span>
          </label>
        ))}
        {shown.length === 0 && <p className="field-hint">{t('No pupil matches.')}</p>}
      </div>
    </fieldset>
  );
}

function LinkNote({ result }: { result: Invited }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  if (result.emailed) return <p className="field-hint" role="status">{t('Invitation emailed to {email}.', { email: result.parent.email })}</p>;
  return (
    <div className="parent-link" role="status">
      <p style={{ margin: 0 }}>{t('Email is not set up here, so send the parent this link yourself. They sign in or create an account with {email}.', { email: result.parent.email })}</p>
      <div className="toolbar">
        <code>{result.link}</code>
        <button type="button" className="btn secondary" onClick={() => { void navigator.clipboard?.writeText(result.link).then(() => setCopied(true)); }}>
          {copied ? t('Copied') : t('Copy link')}
        </button>
      </div>
    </div>
  );
}

export default function ParentsTab() {
  const t = useT();
  const [parents, setParents] = useState<Parent[] | null>(null);
  const [pupils, setPupils] = useState<Pupil[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [access, setAccess] = useState<ParentAccess>({ play: true, edit: false, talk: false });
  const [result, setResult] = useState<Invited | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ learnerIds: string[]; access: ParentAccess } | null>(null);
  const [removing, setRemoving] = useState<Parent | null>(null);

  const load = useCallback(async () => {
    try {
      const [list, kids] = await Promise.all([
        api<Parent[]>('/school/parents', { token: true }),
        api<Pupil[]>('/school/pupils', { token: true }),
      ]);
      setParents(list);
      setPupils(kids);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not load the parents list.'));
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  async function run<T>(action: () => Promise<T>): Promise<T | null> {
    setError(null);
    setBusy(true);
    try {
      const out = await action();
      await load();
      return out;
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That did not work. Please try again.'));
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function invite(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || chosen.length === 0) return;
    setResult(null);
    const r = await run(() => api<Invited>('/school/parents', {
      method: 'POST', token: true, body: { email: email.trim(), name: name.trim() || null, learnerIds: chosen, ...access },
    }));
    if (r) { setResult(r); setEmail(''); setName(''); setChosen([]); setAccess({ play: true, edit: false, talk: false }); }
  }

  async function resend(p: Parent) {
    setResult(null);
    const r = await run(() => api<Invited>(`/school/parents/${p.id}/invite`, { method: 'POST', token: true }));
    if (r) setResult(r);
  }

  async function saveDraft(p: Parent) {
    if (!draft) return;
    const ok = await run(() => api(`/school/parents/${p.id}`, { method: 'PATCH', token: true, body: { learnerIds: draft.learnerIds, ...draft.access } }));
    if (ok !== null) { setEditing(null); setDraft(null); }
  }

  const nameOf = (id: string) => pupils.find((p) => p.id === id)?.name ?? t('a pupil');

  function accessText(p: Parent): string {
    if (p.play && p.edit && p.talk) return t('Full access');
    const parts = [t('Sees progress')];
    if (p.play) parts.push(t('practises at home'));
    if (p.edit) parts.push(t('changes programs and plans'));
    if (p.talk) parts.push(t('uses the Talk Board'));
    return parts.join(', ');
  }

  function status(p: Parent): string {
    if (!p.active) return t('Access removed');
    if (!p.signedIn) return t('Invited, has not signed in yet');
    if (p.lastSeenAt) return t('Last signed in {date}', { date: new Date(p.lastSeenAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) });
    return t('Signed in');
  }

  return (
    <div className="spaced">
      <div className="card">
        <h3>{t('Parents')}</h3>
        <p className="field-hint" style={{ marginTop: 0 }}>
          {t('Parents see only their own child. They sign in with their own email address, not a school account.')}
        </p>
        {error && <div className="error" role="alert">{error}</div>}
        {result && <LinkNote result={result} />}
        {parents === null ? (
          <p className="muted">{t('Loading…')}</p>
        ) : parents.length === 0 ? (
          <p className="muted">{t('No parents invited yet.')}</p>
        ) : (
          <ul className="list">
            {parents.map((p) => (
              <li className="list-item parent-item" key={p.id}>
                <div className="list-item-text">
                  <div className="name">{p.name ?? p.email}</div>
                  {p.name && <div className="muted">{p.email}</div>}
                  <div className="muted">{t('Parent of {names}', { names: p.learnerIds.map(nameOf).join(', ') })}</div>
                  <div className="muted">{accessText(p)} · {status(p)}</div>
                </div>
                <div className="toolbar">
                  {p.active ? (
                    <>
                      <button type="button" className="btn secondary" disabled={busy}
                        aria-expanded={editing === p.id}
                        onClick={() => {
                          if (editing === p.id) { setEditing(null); setDraft(null); return; }
                          setEditing(p.id);
                          setDraft({ learnerIds: p.learnerIds, access: { play: p.play, edit: p.edit, talk: p.talk } });
                        }}>
                        {editing === p.id ? t('Close') : t('Change access')}
                      </button>
                      {!p.signedIn && <button type="button" className="btn ghost" disabled={busy} onClick={() => void resend(p)}>{t('Send invitation again')}</button>}
                      <button type="button" className="btn quiet-danger" disabled={busy} onClick={() => setRemoving(p)}>{t('Remove access')}</button>
                    </>
                  ) : (
                    <button type="button" className="btn secondary" disabled={busy}
                      onClick={() => void run(() => api(`/school/parents/${p.id}`, { method: 'PATCH', token: true, body: { active: true } }))}>
                      {t('Give access again')}
                    </button>
                  )}
                </div>
                {editing === p.id && draft && (
                  <div className="parent-edit">
                    <PupilPicker id={`edit-${p.id}`} pupils={pupils} chosen={draft.learnerIds} onChange={(ids) => setDraft({ ...draft, learnerIds: ids })} />
                    <AccessFields id={`edit-${p.id}`} value={draft.access} onChange={(a) => setDraft({ ...draft, access: a })} />
                    <div className="toolbar">
                      <button type="button" className="btn" disabled={busy || draft.learnerIds.length === 0} onClick={() => void saveDraft(p)}>{t('Save changes')}</button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <form className="card spaced" onSubmit={invite}>
        <h3>{t('Invite a parent')}</h3>
        <div className="field">
          <label htmlFor="parent-new-email">{t('Parent\'s email address')}</label>
          <input id="parent-new-email" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="parent-new-name">{t('Name (optional)')}</label>
          <input id="parent-new-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </div>
        <PupilPicker id="parent-new" pupils={pupils} chosen={chosen} onChange={setChosen} />
        <AccessFields id="parent-new" value={access} onChange={setAccess} />
        <div className="toolbar">
          <button type="submit" className="btn" disabled={busy || !email.trim() || chosen.length === 0}>{t('Invite parent')}</button>
        </div>
      </form>

      <ConfirmDialog
        open={removing !== null}
        title={t('Remove access for {name}?', { name: removing?.name ?? removing?.email ?? '' })}
        message={t('They are signed out and can no longer see their child in EasyLearning. Nothing is deleted. You can give access again later.')}
        confirmLabel={t('Remove access')}
        destructive
        onConfirm={() => {
          const p = removing;
          setRemoving(null);
          if (p) void run(() => api(`/school/parents/${p.id}`, { method: 'PATCH', token: true, body: { active: false } }));
        }}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
